import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Label,
  RadioGroup,
  RadioGroupItem,
} from '@ais-ui/ui-core';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { StepperFooter } from './StepperFooter';
import { StepperHeadline } from './StepperHeadline';
import React, { useEffect, useMemo } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import { useCreateReportContext } from './CreateReportContext';
import {
  createReportTemplateShipFormSchema,
  reportTemplateShipValues,
} from '../utils/createShipsFormSchema';
import { MultiShipCommand } from '../../../utils/ShipCommand';
import { ShowTracksFormField } from '../components/ShowTracksFormField';

export const ShipStep = () => {
  const { messages } = useLocalization();

  const {
    stepperProps,
    setShips,
    ships,
    shipAttribute,
    setShipAttribute,
    chosenReportType,
    miscellaneous,
    setMiscellaneous,
  } = useCreateReportContext();

  const hasShowTracks = !!chosenReportType?.miscellaneous?.['showTracks'];

  const formSchema = useMemo(() => createReportTemplateShipFormSchema(), []);

  const form = useForm<reportTemplateShipValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      ships: [],
      attribute: 'filterBy',
      showTracks: false,
    },
  });

  useEffect(() => {
    form.reset({
      ships: ships,
      attribute: shipAttribute,
      showTracks: (miscellaneous['showTracks'] as boolean) ?? false,
    });
  }, [ships, shipAttribute, miscellaneous, form]);

  const onPrevious = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    stepperProps.prevStep();
  };

  const onNext = (data: reportTemplateShipValues) => {
    setShips(data.ships);
    setShipAttribute(data.attribute);
    if (hasShowTracks) {
      setMiscellaneous((prev) => ({
        ...prev,
        showTracks: data.showTracks ?? false,
      }));
    }
    stepperProps.nextStep();
  };

  return (
    <div className="grid grid-rows-[auto_1fr_auto] h-full">
      <StepperHeadline
        headerText={messages.reportsCreateReportShipTitle}
        subHeaderText={messages.reportsCreateReportShipSubtitle}
      />
      <Form {...form}>
        <form className="flex flex-col space-y-8 pt-4">
          <div className="space-y-6">
            <FormField
              control={form.control}
              name="ships"
              render={({ field }) => (
                <FormItem className="flex flex-col">
                  <div className="flex items-center justify-between">
                    <FormLabel>
                      {messages.reportsCreateReportShipLabel}
                    </FormLabel>
                    <FormField
                      control={form.control}
                      name="attribute"
                      render={({ field: attrField }) => {
                        const hasVessels =
                          field.value && field.value.length > 0;
                        return (
                          <div className="flex items-center gap-4">
                            <RadioGroup
                              value={attrField.value}
                              onValueChange={attrField.onChange}
                              className="flex items-center gap-4"
                              disabled={!hasVessels}
                            >
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem
                                  value="filterBy"
                                  id="filterBy"
                                />
                                <Label
                                  htmlFor="filterBy"
                                  className={`text-sm ${hasVessels ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                                >
                                  Filter by
                                </Label>
                              </div>
                              <div className="flex items-center space-x-2">
                                <RadioGroupItem value="colorBy" id="colorBy" />
                                <Label
                                  htmlFor="colorBy"
                                  className={`text-sm ${hasVessels ? 'cursor-pointer' : 'cursor-not-allowed opacity-50'}`}
                                >
                                  Highlight
                                </Label>
                              </div>
                            </RadioGroup>
                            <InfoIconTooltip>
                              {!hasVessels
                                ? 'Select at least one vessel to enable this option'
                                : attrField.value === 'colorBy'
                                  ? 'Highlights chosen vessels by giving them a different color'
                                  : 'Only shows the vessels that were chosen'}
                            </InfoIconTooltip>
                          </div>
                        );
                      }}
                    />
                  </div>
                  <FormControl>
                    <MultiShipCommand
                      value={field.value}
                      onChange={field.onChange}
                    ></MultiShipCommand>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {hasShowTracks && (
              <ShowTracksFormField
                form={form}
                name="showTracks"
                fieldConfig={chosenReportType?.miscellaneous?.['showTracks']}
              />
            )}
          </div>
        </form>
      </Form>
      <StepperFooter
        onPrevious={onPrevious}
        onNext={form.handleSubmit(onNext)}
        stepperProps={stepperProps}
      />
    </div>
  );
};
